import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { QRCodeSVG } from 'qrcode.react';

export default function ShippingLabel() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const shouldPrint = searchParams.get('print') === 'true';
  const [error, setError] = useState(null);
  const [pkg, setPkg] = useState(null);
  const printRef = React.useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackage = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'packages', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const packageData = { id: docSnap.id, ...docSnap.data() };
          setPkg(packageData);
        } else {
          setError('Package not found');
        }
      } catch (err) {
        console.error('Error fetching package:', err);
        setError('Failed to load package details');
      } finally {
        setLoading(false);
      }
    };

    fetchPackage();
  }, [id]);

  const handleDownloadPdf = async () => {
    const element = printRef.current;
    if (!element) return;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      letterRendering: true
    });

    const data = canvas.toDataURL("image/png");

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "px",
      format: "a4",
    });

    const imgProperties = pdf.getImageProperties(data);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProperties.height * pdfWidth) / imgProperties.width;

    pdf.addImage(data, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`shipping-label-${pkg.trackingNumber}.pdf`);
  };

  useEffect(() => {
    const handleAutoPrint = async () => {
      if (shouldPrint && !loading && pkg) {
        setTimeout(() => {
          window.print();
          setTimeout(() => navigate(-1), 100);
        }, 800);
      }
    };

    handleAutoPrint();
  }, [shouldPrint, loading, pkg, navigate]);

  const formatDate = (seconds) => {
    if (!seconds) return 'N/A';
    return new Date(seconds * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-8 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 p-8 flex flex-col items-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!pkg) return null;

  return (
    <>
      <style>
        {`
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }

            body {
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
            }

            .print-content {
              width: 100%;
              height: 100%;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
              position: relative !important;
              overflow: hidden !important;
            }

            .rotated-label {
              transform: rotate(90deg);
              transform-origin: center;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              padding: 16px;
              max-width: 100vh;
              max-height: 100vw;
            }

            .rotated-label > div {
              width: auto;
              max-width: 100%;
            }

            .print-content p {
              font-size: 18pt !important;
              line-height: 1.2 !important;
              color: #000 !important;
              margin-bottom: 10pt !important;
            }

            .qr-container {
              width: 210px !important;
              padding: 12px !important;
              border: 1px solid #000 !important;
              flex-shrink: 0 !important;
            }

            .qr-code-wrapper {
              width: 180px !important;
              height: 180px !important;
            }

            .qr-code-wrapper svg {
              width: 170px !important;
              height: 170px !important;
            }

            .tracking-text {
              font-size: 14pt !important;
              font-family: monospace !important;
              color: #000 !important;
            }

            .no-print {
              display: none !important;
            }

            @page {
              size: A4 portrait;
              margin: 0;
            }
          }
        `}
      </style>

      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <div
          className="bg-white shadow-lg"
          style={{
            width: '210mm',
            minHeight: '297mm',
            margin: '0 auto',
            position: 'relative',
            boxSizing: 'border-box',
            pageBreakAfter: 'avoid',
            pageBreakInside: 'avoid',
          }}
        >
          <div ref={printRef} className="bg-white print-content">
            <div className="rotated-label">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '24px', width: '100%' }}>
                <div style={{ flex: '1' }}>
                  <p style={{ fontSize: '12pt', color: '#4B5563' }}>Date: {formatDate(pkg.createdAt?.seconds)}</p>
                  <p style={{ fontSize: '12pt', color: '#4B5563' }}>Recipient: {pkg.recipient || 'N/A'}</p>
                  <p style={{ fontSize: '12pt', color: '#4B5563' }}>Sender: {pkg.sender || 'N/A'}</p>
                  <p style={{ fontSize: '12pt', color: '#4B5563' }}>Phone Number: {pkg.phone || ''}</p>
                  <p style={{ fontSize: '12pt', color: '#4B5563' }}>Details: {pkg.details || ''}</p>
                  <p style={{ fontSize: '12pt', color: '#4B5563' }}>Destination: {pkg.destination || 'N/A'}</p>
                  <p style={{ fontSize: '12pt', color: '#4B5563' }}>Total: TT$ {pkg.total || ''}</p>
                </div>
                <div
                  className="qr-container"
                  style={{
                    padding: '16px',
                    width: '180px',
                    alignSelf: 'flex-start',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                >
                  <div
                    className="qr-code-wrapper"
                    style={{
                      width: '150px',
                      height: '150px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#F9FAFB',
                      marginBottom: '8px',
                      borderRadius: '8px',
                    }}
                  >
                    {pkg.trackingNumber ? (
                      <QRCodeSVG 
                        value={"https://pacepaktt.com/package/" + pkg.id}
                        size={130}
                        level="H"
                        fgColor="#000000"
                        bgColor="#FFFFFF"
                      />
                    ) : (
                      <p style={{ color: '#9CA3AF', fontSize: '12px' }}>
                        No tracking number available
                      </p>
                    )}
                  </div>
                  <p className="tracking-text" style={{ 
                    fontSize: '12px', 
                    color: '#6B7280', 
                    wordBreak: 'break-all', 
                    fontFamily: 'monospace',
                    margin: '0'
                  }}>
                    {pkg.trackingNumber}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {!shouldPrint && (
          <div className="mt-6 no-print">
            <button
              onClick={handleDownloadPdf}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Download PDF
            </button>
          </div>
        )}
      </div>
    </>
  );
}
